import * as React from 'react'

import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import SectionHeader from '@/components/common/section-header'
import { Accordion, AccordionContent, AccordionItem, AccordionTriggerV2 } from '@/components/ui/accordion'

import type { SectionElementSchema } from '@/types/element-schema.type'

interface FaqItem {
  id: string | number
  question: string
  answer: string
}

interface FaqQuestionAnswerSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Section heading */
  heading?: string
  /** Data source: 'api' or 'custom' */
  dataSource?: 'api' | 'custom'
  /** Number of FAQs to show (API mode) */
  pageSize?: number
  /** Category filter (API mode) */
  category?: string
  /** Custom FAQs (custom mode) */
  faqs?: FaqItem[]
  /** Expand mode: 'single' or 'multiple' */
  expandMode?: 'single' | 'multiple'
}

const defaultFaqItems: FaqItem[] = [
  {
    id: 1,
    question: 'Khóa học này dành cho ai?',
    answer:
      'Khóa học phù hợp cho tất cả mọi người, từ người mới bắt đầu đến người đã có kinh nghiệm muốn nâng cao kỹ năng.',
  },
  {
    id: 2,
    question: 'Thời gian học là bao lâu?',
    answer:
      'Thời gian học tùy thuộc vào từng khóa học, thường từ 2-6 tháng. Bạn có thể học theo tốc độ riêng của mình.',
  },
  {
    id: 3,
    question: 'Có hỗ trợ sau khi hoàn thành khóa học không?',
    answer:
      'Có, chúng tôi hỗ trợ học viên ngay cả sau khi hoàn thành khóa học thông qua cộng đồng và các buổi mentoring.',
  },
  {
    id: 4,
    question: 'Hình thức thanh toán như thế nào?',
    answer:
      'Chúng tôi hỗ trợ nhiều hình thức thanh toán: chuyển khoản ngân hàng, thẻ tín dụng, và trả góp 0% lãi suất.',
  },
]

// Element schema for Puck editor
export const faqQuestionAnswerSectionSchema: SectionElementSchema = {
  componentType: 'FaqQuestionAnswerSection',
  label: 'FAQ Section',
  category: 'content',
  description: 'FAQ accordion section',
  elements: [
    {
      key: 'heading',
      type: 'textarea',
      label: 'Section Heading',
      default: 'Câu hỏi thường gặp',
    },
    {
      key: 'dataSource',
      type: 'select',
      label: 'Data Source',
      options: [
        { label: 'From API', value: 'api' },
        { label: 'Custom Items', value: 'custom' },
      ],
      default: 'custom',
    },
    {
      key: 'pageSize',
      type: 'select',
      label: 'Number of FAQs',
      helpText: 'Maximum items to display (API mode)',
      options: [
        { label: '3 FAQs', value: 3 },
        { label: '5 FAQs', value: 5 },
        { label: '8 FAQs', value: 8 },
        { label: '10 FAQs', value: 10 },
      ],
      default: 5,
    },
    {
      key: 'category',
      type: 'select',
      label: 'Category Filter',
      helpText: 'Filter FAQs by category (requires API integration)',
      options: [
        { label: 'All Categories', value: '' },
        { label: 'General', value: 'general' },
        { label: 'Pricing', value: 'pricing' },
        { label: 'Courses', value: 'courses' },
        { label: 'Technical', value: 'technical' },
      ],
      default: '',
    },
    {
      key: 'faqs',
      type: 'array',
      label: 'Custom FAQs',
      helpText: 'Add, edit, or remove FAQ items',
      default: defaultFaqItems,
      children: [
        { key: 'question', type: 'textarea', label: 'Question', required: true },
        { key: 'answer', type: 'textarea', label: 'Answer', required: true },
      ],
    },
    {
      key: 'expandMode',
      type: 'select',
      label: 'Expand Mode',
      helpText: 'Single = only one open at a time, Multiple = any can be open',
      options: [
        { label: 'Single', value: 'single' },
        { label: 'Multiple', value: 'multiple' },
      ],
      default: 'multiple',
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
    },
  ],
}

sectionSchemaRegistry.register(faqQuestionAnswerSectionSchema)

const FaqQuestionAnswerSection = React.forwardRef<HTMLDivElement, FaqQuestionAnswerSectionProps>(
  (
    {
      className,
      heading,
      dataSource = 'custom',
      pageSize = 5,
      category,
      faqs: customFaqs,
      expandMode = 'multiple',
      ...props
    },
    ref
  ) => {
    const { text } = useLocale()

    // Default FAQs from locale
    const defaultFaqs: FaqItem[] = [
      {
        id: 1,
        question: text.MAIN.BODY.FAQ[1].QUESTION,
        answer: text.MAIN.BODY.FAQ[1].ANSWER,
      },
      {
        id: 2,
        question: text.MAIN.BODY.FAQ[2].QUESTION,
        answer: text.MAIN.BODY.FAQ[2].ANSWER,
      },
      {
        id: 3,
        question: text.MAIN.BODY.FAQ[3].QUESTION,
        answer: text.MAIN.BODY.FAQ[3].ANSWER,
      },
      {
        id: 4,
        question: text.MAIN.BODY.FAQ[4].QUESTION,
        answer: text.MAIN.BODY.FAQ[4].ANSWER,
      },
    ]

    // Filter by category if provided (when API mode is implemented, this would filter API results)
    // For now, category filter is a placeholder for future API integration
    const filteredFaqs = category
      ? defaultFaqs // In future: filter by category from API response
      : defaultFaqs

    // Determine which data to use
    // Ensure each FAQ item has an id (editor-created items may lack one)
    const rawFaqs = dataSource === 'custom' && customFaqs?.length ? customFaqs : filteredFaqs.slice(0, pageSize)
    const displayFaqs = rawFaqs.map((faq, index) => ({ ...faq, id: faq.id ?? index + 1 }))

    const displayHeading = heading || text.MAIN.BODY.HEADING_11

    return (
      <div className={cn('container sm:pb-14 pb-4 mt-8 sm:mt-16', className)} id="faq" ref={ref} {...props}>
        <div className="max-w-6xl mx-auto px-4">
          <SectionHeader title={displayHeading} />
          <div className="flex flex-col items-center justify-center mt-8">
            <Accordion type={expandMode} className="w-full md:w-3/4">
              {displayFaqs.map(faq => (
                <AccordionItem value={`item-${faq.id}`} key={faq.id}>
                  <AccordionTriggerV2 className="text-start text-base sm:text-xl">{faq.question}</AccordionTriggerV2>
                  <AccordionContent className="text-base text-muted-foreground">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    )
  }
)

FaqQuestionAnswerSection.displayName = 'FaqQuestionAnswerSection'

export default FaqQuestionAnswerSection
